import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { BoltDirectivesModule } from '@nationwide-bolt/ng-directives';

import { API_ERROR_MESSAGES } from '@hsc/common/constants/api-error-messages';
import { API_ENDPOINTS } from '@hsc/common/constants/endpoints';
import { PRODUCT_INFO } from '@hsc/common/constants/product-info';
import { REGEX } from '@hsc/common/constants/regex';
import { PcPolicyNumberLambdaResponse } from '@hsc/common/schemas/pc-management.schema';
import { PcSubmissionNumberLambdaResponse } from '@hsc/common/schemas/pc-submission.schema';
import { USER_NOTIFICATIONS } from '@constants/user-notifications';
import { ApigeeService } from '@services/apigee.service';
import { FullStoryService } from '@services/fullstory.service';
import { clearFormValidationError, collectFormValidationErrors } from '@utilities/form-utilities';
import { ApigeeError } from '@utilities/http-utilities';

import { SentenceCasePipe } from '../../../../pipes/sentence-case.pipe';
import { QuotingFlowStore } from '../../services/quoting-flow.store';
import { CLIENT_LOOKUP_TYPES } from '../../types/quoting-flow.types';

@Component({
  selector: 'hsc-client-lookup-step',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [CommonModule, ReactiveFormsModule, BoltDirectivesModule, SentenceCasePipe],
  templateUrl: './client-lookup.step.html',
})
export class ClientLookupStep {
  // Services
  private readonly apigeeService = inject(ApigeeService);
  private readonly fullStoryService = inject(FullStoryService);
  readonly qfStore = inject(QuotingFlowStore);

  // Service Properties
  readonly apigeeIsLoading = this.apigeeService.isLoading;

  // Component Properties
  readonly clientLookupTypes = CLIENT_LOOKUP_TYPES;
  readonly productInfo = PRODUCT_INFO;
  readonly formValidationErrors = signal<Record<string, string>>({});

  private get form() {
    return this.qfStore.clientLookupForm;
  }
  private get lookupType() {
    return this.form.controls.lookupType;
  }
  private get lookupValue() {
    return this.form.controls.lookupValue;
  }
  get searchIndicator(): string {
    if (this.qfStore.isLookupSuccessful()) {
      return 'success';
    } else if (this.apigeeIsLoading()) {
      return 'waiting';
    }
    return '';
  }

  constructor() {
    // Conditionally swap validation for lookup value based on lookup type
    this.lookupType.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      const pattern = this.qfStore.isLookupTypePolicy() ? REGEX.policyNumber : REGEX.submissionNumber;
      this.lookupValue.setValidators([Validators.required, Validators.pattern(pattern)]);
      this.lookupValue.updateValueAndValidity();
    });
  }

  // Component Methods
  resetClientLookupState(): void {
    this.qfStore.setNotificationState(null);
    clearFormValidationError(this.formValidationErrors, 'lookupValue');
    this.qfStore.resetClientLookupStep(false);
  }

  private get customFormValidationErrorMessages() {
    return {
      lookupValue: {
        pattern: this.qfStore.isLookupTypePolicy() ? 'Format: Alpha numeric (1234AA567890)' : 'Format: Numeric',
      },
    };
  }

  private validateClientLookupForm(): boolean {
    this.form.markAllAsTouched();
    const errors = collectFormValidationErrors(this.form, this.customFormValidationErrorMessages);
    if (Object.keys(errors).length > 0) {
      this.formValidationErrors.set(errors);
      return false;
    }
    return true;
  }

  handleClientLookupSearch(): void {
    // If matched, allow progression
    if (this.qfStore.isLookupSuccessful()) {
      this.handleForward();
      return;
    }

    // Reset any lingering notification/validation state
    this.resetClientLookupState();

    // Trim any accidental whitespaces
    this.lookupValue.setValue(this.lookupValue.value.trim());

    // Validate form before proceeding
    if (!this.validateClientLookupForm()) return;

    // Capture lookupType at request time so no impact from in-flight changes
    const isLookupTypePolicy = this.qfStore.isLookupTypePolicy();

    // Perform the appropriate API call based on lookupType
    const endpoint = isLookupTypePolicy
      ? API_ENDPOINTS.pcPolicyNumber.invoke(this.lookupValue.value)
      : API_ENDPOINTS.pcSubmissionNumber.invoke(this.lookupValue.value);

    // Disable lookupType while searching so value can't be changed
    // Resets on error or in input change
    this.lookupType.disable();

    this.apigeeService.get<PcPolicyNumberLambdaResponse | PcSubmissionNumberLambdaResponse>(endpoint).subscribe({
      next: (response) => this.handleClientLookupSuccess(response, isLookupTypePolicy),
      error: (error: ApigeeError) => this.handleClientLookupError(error, isLookupTypePolicy),
    });
  }

  private handleClientLookupSuccess(
    response: PcPolicyNumberLambdaResponse | PcSubmissionNumberLambdaResponse,
    isLookupTypePolicy: boolean,
  ): void {
    if (isLookupTypePolicy) {
      this.qfStore.patchFromPolicyLookup(response as PcPolicyNumberLambdaResponse);
    } else {
      this.qfStore.patchFromSubmissionLookup(response as PcSubmissionNumberLambdaResponse);
    }

    this.qfStore.isLookupSuccessful.set(true);
    this.fullStoryService.event('Client Lookup Succeeded', {
      lookupType: isLookupTypePolicy ? CLIENT_LOOKUP_TYPES.POLICY_NUMBER : CLIENT_LOOKUP_TYPES.SUBMISSION_NUMBER,
    });
  }

  private trackErrors(isLookupTypePolicy: boolean, error: string): void {
    this.fullStoryService.event('Client Lookup Failed', {
      lookupType: isLookupTypePolicy ? CLIENT_LOOKUP_TYPES.POLICY_NUMBER : CLIENT_LOOKUP_TYPES.SUBMISSION_NUMBER,
      reason: error,
    });
  }

  private handleClientLookupError(error: ApigeeError, isLookupTypePolicy: boolean): void {
    this.lookupType.enable();
    switch (error.message) {
      case API_ERROR_MESSAGES.AGENCY_NOT_PILOT_ELIGIBLE:
        this.qfStore.setNotificationState(USER_NOTIFICATIONS.agencyNotPilotEligible);
        this.trackErrors(isLookupTypePolicy, 'agencyNotPilotEligible');
        break;
      case API_ERROR_MESSAGES.AGENT_AGENCY_MISMATCH:
        this.qfStore.setNotificationState(USER_NOTIFICATIONS.agentAgencyMismatch(isLookupTypePolicy), 'lookupValue');
        this.trackErrors(isLookupTypePolicy, 'agentAgencyMismatch');
        break;
      case API_ERROR_MESSAGES.ECIF_NOT_FOUND:
      case API_ERROR_MESSAGES.POLICY_NOT_FOUND:
        this.qfStore.setNotificationState(USER_NOTIFICATIONS.policyNotFound, 'lookupValue');
        this.trackErrors(isLookupTypePolicy, 'policyNotFound');
        break;
      case API_ERROR_MESSAGES.POLICY_NOT_ACTIVE:
        this.qfStore.setNotificationState(USER_NOTIFICATIONS.policyNotActive, 'lookupValue');
        this.trackErrors(isLookupTypePolicy, 'policyNotActive');
        break;
      case API_ERROR_MESSAGES.SUBMISSION_NOT_ACTIVE:
        this.qfStore.setNotificationState(USER_NOTIFICATIONS.submissionNotActive, 'lookupValue');
        this.trackErrors(isLookupTypePolicy, 'submissionNotActive');
        break;
      case API_ERROR_MESSAGES.SUBMISSION_NOT_FOUND:
        this.qfStore.setNotificationState(USER_NOTIFICATIONS.submissionNotFound, 'lookupValue');
        this.trackErrors(isLookupTypePolicy, 'submissionNotFound');
        break;
      default:
        this.qfStore.setNotificationState(USER_NOTIFICATIONS.systemError);
        this.trackErrors(isLookupTypePolicy, 'systemError');
    }
  }

  private handleForward(): void {
    this.qfStore.handleForward();
  }
}
